import React, { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SearchProvider } from "@/contexts/SearchContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PremiumProvider } from "@/contexts/PremiumContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

// Eagerly load homepage
import Index from "./pages/Index";

// Lazy load everything else
const NotFound = lazy(() => import("./pages/NotFound"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const SearchResults = lazy(() => import("./pages/SearchResults"));
const Auth = lazy(() => import("./pages/Auth"));
const LegalHub = lazy(() => import("./pages/LegalHub"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const MerchantTerms = lazy(() => import("./pages/MerchantTerms"));
const POPIACompliance = lazy(() => import("./pages/POPIACompliance"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const CustomerHelp = lazy(() => import("./pages/help/CustomerHelp"));
const DriverHelp = lazy(() => import("./pages/help/DriverHelp"));
const MerchantHelp = lazy(() => import("./pages/help/MerchantHelp"));

// Customer Pages
const StorePage = lazy(() => import("./pages/customer/RestaurantPage"));
const CheckoutPage = lazy(() => import("./pages/customer/CheckoutPage"));
const OrderTrackingPage = lazy(() => import("./pages/customer/OrderTrackingPage"));
const BrowsePage = lazy(() => import("./pages/customer/BrowsePage"));
const FavoritesPage = lazy(() => import("./pages/customer/FavoritesPage"));
const OrdersPage = lazy(() => import("./pages/customer/OrdersPage"));
const SettingsPage = lazy(() => import("./pages/customer/SettingsPage"));

// Vendor Pages
const VendorDashboard = lazy(() => import("./pages/vendor/VendorDashboard"));
const VendorOrders = lazy(() => import("./pages/vendor/VendorOrders"));
const VendorAnalytics = lazy(() => import("./pages/vendor/VendorAnalytics"));
const VendorMenu = lazy(() => import("./pages/vendor/VendorMenu"));
const VendorSettings = lazy(() => import("./pages/vendor/VendorSettings"));

// Driver Pages
const DriverDashboard = lazy(() => import("./pages/driver/DriverDashboard"));
const DriverEarnings = lazy(() => import("./pages/driver/DriverEarnings"));
const DriverDeliveries = lazy(() => import("./pages/driver/DriverDeliveries"));
const DriverAvailableOrders = lazy(() => import("./pages/driver/DriverAvailableOrders"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));

// Onboarding Pages
const VendorOnboarding = lazy(() => import("./pages/onboarding/VendorOnboarding"));
const DriverOnboarding = lazy(() => import("./pages/onboarding/DriverOnboarding"));

// Other Pages
const FAQ = lazy(() => import("./pages/FAQ"));
const Profile = lazy(() => import("./pages/Profile"));
const About = lazy(() => import("./pages/About"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PremiumProvider>
        <LanguageProvider>
          <SearchProvider>
            <CartProvider>
              <BrowserRouter>
              <TooltipProvider>
                <div className="min-h-screen bg-background text-foreground">
                  <Toaster />
                  <Sonner />
                  <ScrollToTop />
                  <Suspense fallback={<div className="min-h-screen bg-background" />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/legal" element={<LegalHub />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/merchant-terms" element={<MerchantTerms />} />
                  <Route path="/popia-compliance" element={<POPIACompliance />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route path="/help" element={<HelpCenter />} />
                  <Route path="/help/customers" element={<CustomerHelp />} />
                  <Route path="/help/drivers" element={<DriverHelp />} />
                  <Route path="/help/merchants" element={<MerchantHelp />} />
                  <Route path="/install" element={<InstallApp />} />
                  <Route path="/search" element={<SearchResults />} />
                  
                  {/* Onboarding Routes */}
                  <Route path="/onboard/vendor" element={
                    <ProtectedRoute>
                      <VendorOnboarding />
                    </ProtectedRoute>
                  } />
                  <Route path="/onboard/driver" element={
                    <ProtectedRoute>
                      <DriverOnboarding />
                    </ProtectedRoute>
                  } />
                  
                  {/* Customer Routes */}
                  <Route path="/browse" element={<BrowsePage />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/store/:id" element={<StorePage />} />
                  <Route path="/restaurant/:id" element={<StorePage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order-tracking/:orderId" element={<OrderTrackingPage />} />
                  
                  {/* Vendor Routes - Protected */}
                  <Route path="/vendor" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/dashboard" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/orders" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorOrders />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/analytics" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorAnalytics />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/menu" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorMenu />
                    </ProtectedRoute>
                  } />
                  <Route path="/vendor/settings" element={
                    <ProtectedRoute requiredRole="vendor">
                      <VendorSettings />
                    </ProtectedRoute>
                  } />
                  
                  {/* Driver Routes - Protected */}
                  <Route path="/driver" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/earnings" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverEarnings />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/deliveries" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverDeliveries />
                    </ProtectedRoute>
                  } />
                  <Route path="/driver/available-orders" element={
                    <ProtectedRoute requiredRole="driver">
                      <DriverAvailableOrders />
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin Routes - Protected */}
                  <Route path="/admin/dashboard" element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  
                  {/* Other Routes */}
                  <Route path="/about" element={<About />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </div>
              </TooltipProvider>
              </BrowserRouter>
            </CartProvider>
          </SearchProvider>
        </LanguageProvider>
      </PremiumProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
